import "./Card.css";
import { Link } from "react-router-dom";
import SaveButton from "./SaveButton";

const weekdayShort = new Intl.DateTimeFormat("fr-FR", { weekday: "short" });
const dayMonth = new Intl.DateTimeFormat("fr-FR", {
  day: "numeric",
  month: "short",
});
const dayMonthYear = new Intl.DateTimeFormat("fr-FR", {
  day: "numeric",
  month: "short",
  year: "numeric",
});
const timeFmt = new Intl.DateTimeFormat("fr-FR", {
  hour: "2-digit",
  minute: "2-digit",
});

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

// "20h00", or "" for an all-day event (the dataset uses 00:00 for those).
function hourLabel(date) {
  if (date.getHours() === 0 && date.getMinutes() === 0) return "";
  const parts = timeFmt.formatToParts(date);
  const get = (type) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("hour")}h${get("minute")}`;
}

// Compact surtitle for a card: "Mar. 20 oct. · 20h00", "20 et 21 oct.",
// "Du 20 oct. au 31 déc.".
export function formatCardDate(startStr, endStr) {
  const start = new Date(startStr);
  if (Number.isNaN(start.getTime())) return "";

  const end = endStr ? new Date(endStr) : null;
  const endValid = end && !Number.isNaN(end.getTime());

  if (!endValid || startOfDay(end) === startOfDay(start)) {
    const day = `${weekdayShort.format(start)} ${dayMonth.format(start)}`;
    const hour = hourLabel(start);
    return hour ? `${day} · ${hour}` : day;
  }

  const dayDiff = Math.round((startOfDay(end) - startOfDay(start)) / 86400000);
  if (dayDiff === 1) {
    return `${dayMonth.format(start)} et ${dayMonth.format(end)}`;
  }

  const endFormatter =
    end.getFullYear() === start.getFullYear() ? dayMonth : dayMonthYear;
  return `Du ${dayMonth.format(start)} au ${endFormatter.format(end)}`;
}

export function priceBadge(priceType) {
  if (priceType === "gratuit") {
    return { label: "Gratuit", variant: "pa-badge--free" };
  }
  if (priceType === "payant") {
    return { label: "Payant", variant: "pa-badge--paid" };
  }
  if (typeof priceType === "string" && priceType.startsWith("gratuit")) {
    return { label: "Gratuit sous condition", variant: "pa-badge--muted" };
  }
  return null;
}

function Card({ content }) {
  const f = content.fields;
  const date = formatCardDate(f.date_start, f.date_end);
  const price = priceBadge(f.price_type);

  return (
    <article className="pa-card">
      <Link
        to={`/event/${content.id}`}
        className="pa-card__media"
        tabIndex={-1}
        aria-hidden="true"
      >
        {f.cover_url ? (
          <img
            src={f.cover_url}
            className="pa-card__img"
            alt=""
            loading="lazy"
          />
        ) : (
          <span className="pa-card__noimg">
            <i className="bi bi-image" aria-hidden="true"></i>
          </span>
        )}
      </Link>

      {date && <p className="pa-card__date">{date}</p>}

      <h3 className="pa-card__title">
        <Link to={`/event/${content.id}`} className="stretched-link">
          {f.title}
        </Link>
      </h3>

      {f.address_name && (
        <p className="pa-card__venue">
          <i className="bi bi-geo-alt" aria-hidden="true"></i>
          <span>{f.address_name}</span>
        </p>
      )}

      <div className="pa-card__foot">
        {price ? (
          <span className={`pa-badge ${price.variant}`}>{price.label}</span>
        ) : (
          <span aria-hidden="true"></span>
        )}
        <div className="pa-above">
          <SaveButton id={content.id} variant="icon" title={f.title} />
        </div>
      </div>
    </article>
  );
}

export default Card;
